import { useEffect, useRef, useCallback, useState } from 'react';

// ─── Types ───────────────────────────────────────────────────────────────────

type Vec2 = { x: number; y: number };

interface Enemy {
  id: number;
  type: 'missile' | 'alien' | 'bomber';
  pos: Vec2;
  vel: Vec2;
  radius: number;
  hp: number;
  maxHp: number;
  angle: number;      // rotation
  spinSpeed: number;
  points: number;
  exploding: boolean;
  explodeTimer: number;
  trail: Vec2[];
}

interface LaserBeam {
  id: number;
  x: number;
  y: number;
  targetX: number;
  targetY: number;
  timer: number;
  hit: boolean;
}

interface Particle {
  id: number;
  pos: Vec2;
  vel: Vec2;
  life: number;
  maxLife: number;
  color: string;
  radius: number;
}

interface StarField {
  x: number;
  y: number;
  brightness: number;
  twinkle: number;
  size: number;
}

interface Shuttle {
  id: number;
  pos: Vec2;
  vel: Vec2;          // flies left-to-right or right-to-left
  collected: boolean;
  gone: boolean;      // exited screen without being collected
  flashTimer: number; // brief white flash on collect
}

type GameState = 'idle' | 'playing' | 'gameover';

// ─── Constants ───────────────────────────────────────────────────────────────

const STAR_COUNT = 120;
const BASE_SPAWN_INTERVAL = 2200;   // ms
const MIN_SPAWN_INTERVAL   = 600;
const LASER_COOLDOWN = 80;           // ms between shots — rapid fire
const MAX_LIVES = 3;
const MAX_SHIELDS = 6;              // hard cap on shield count
const SHIELD_BONUS_INTERVAL = 2000; // points between shuttle visits
const EARTH_IMG_URL = 'https://blossom.dreamith.to/f1e017d81fe206a6b494214eff69a93e51a9a06349ac1b32ca6c1375ab772828.jpeg';
const LASER_SFX_URL = 'https://blossom.dreamith.to/56b6df4e5c630b60a9eeb7ef35e57ca1ebb9ace9484cb7a38287a5fffd4ea188.mpga';
const EXPLOSION_SFX_URL = 'https://blossom.ditto.pub/3effabb020cd66cc18ee34fd2cd7e6fd961c49834f0120640ad3fba1ee201af2.mpga';
const SHIELD_SFX_URL = 'https://blossom.ditto.pub/2762c5c5082f348499cd053c6145cd840f7e2b94a5b3a19dc380f3d0333b5638.mpga';
const MUSIC_URL = 'https://blossom.ditto.pub/43530d4a9a4afe94af7847fcccddeb93a1497f0d514398fe4f8734e652e12e88.mpga';
const START_SFX_URL = 'https://blossom.ditto.pub/f10d017f0f117488d5e8597e5d70a3fe58e6c960aa0c2b321d3e22f6a9d655a8.mpga';
const DEATH_SFX_URL = 'https://blossom.ditto.pub/d24f2b1492e7e8275ba500644397dba693743cc5bf2fdf21ffb1a78a0d2ff774.mpga';
const EARTH_STRIKE_SFX_URL = 'https://blossom.ditto.pub/04498d3faac75044f330c466a7f759170569c77f20651ba34e55bfe57140ae21.mpga';

// ─── Helpers ─────────────────────────────────────────────────────────────────

let nextId = 1;
const uid = () => nextId++;

function lerp(a: number, b: number, t: number) {
  return a + (b - a) * t;
}

function dist(a: Vec2, b: Vec2) {
  const dx = a.x - b.x;
  const dy = a.y - b.y;
  return Math.sqrt(dx * dx + dy * dy);
}

function generateStars(count: number): StarField[] {
  return Array.from({ length: count }, () => ({
    x: Math.random(),
    y: Math.random() * 0.62,
    brightness: 0.4 + Math.random() * 0.6,
    twinkle: Math.random() * Math.PI * 2,
    size: Math.random() < 0.08 ? 2.5 : Math.random() < 0.25 ? 1.8 : 1.1,
  }));
}

// ─── Drawing helpers ──────────────────────────────────────────────────────────

function drawMissile(ctx: CanvasRenderingContext2D, e: Enemy, scale: number) {
  const s = e.radius * scale;
  ctx.save();
  ctx.translate(e.pos.x, e.pos.y);
  ctx.rotate(e.angle);

  // Body
  ctx.fillStyle = '#c0c0d0';
  ctx.beginPath();
  ctx.ellipse(0, 0, s * 0.35, s, 0, 0, Math.PI * 2);
  ctx.fill();

  // Nose cone
  const noseGrad = ctx.createLinearGradient(0, -s, 0, -s * 0.4);
  noseGrad.addColorStop(0, '#ff4444');
  noseGrad.addColorStop(1, '#cc2222');
  ctx.fillStyle = noseGrad;
  ctx.beginPath();
  ctx.moveTo(0, -s * 1.2);
  ctx.lineTo(s * 0.35, -s * 0.4);
  ctx.lineTo(-s * 0.35, -s * 0.4);
  ctx.closePath();
  ctx.fill();

  // Fins
  ctx.fillStyle = '#888899';
  ctx.beginPath();
  ctx.moveTo(s * 0.35, s * 0.4);
  ctx.lineTo(s * 0.9, s * 1.0);
  ctx.lineTo(s * 0.35, s * 0.8);
  ctx.closePath();
  ctx.fill();
  ctx.beginPath();
  ctx.moveTo(-s * 0.35, s * 0.4);
  ctx.lineTo(-s * 0.9, s * 1.0);
  ctx.lineTo(-s * 0.35, s * 0.8);
  ctx.closePath();
  ctx.fill();

  // Engine glow
  const glow = ctx.createRadialGradient(0, s * 1.05, 0, 0, s * 1.05, s * 0.6);
  glow.addColorStop(0, 'rgba(255,160,30,0.9)');
  glow.addColorStop(0.5, 'rgba(255,80,10,0.5)');
  glow.addColorStop(1, 'rgba(255,40,0,0)');
  ctx.fillStyle = glow;
  ctx.beginPath();
  ctx.arc(0, s * 1.05, s * 0.6, 0, Math.PI * 2);
  ctx.fill();

  ctx.restore();
}

function drawAlien(ctx: CanvasRenderingContext2D, e: Enemy, t: number, scale: number) {
  const s = e.radius * scale;
  ctx.save();
  ctx.translate(e.pos.x, e.pos.y);
  ctx.rotate(e.angle);

  // Saucer body
  const bodyGrad = ctx.createRadialGradient(0, -s * 0.1, s * 0.1, 0, 0, s);
  bodyGrad.addColorStop(0, '#88ffcc');
  bodyGrad.addColorStop(0.5, '#228855');
  bodyGrad.addColorStop(1, '#115533');
  ctx.fillStyle = bodyGrad;
  ctx.beginPath();
  ctx.ellipse(0, 0, s, s * 0.35, 0, 0, Math.PI * 2);
  ctx.fill();

  // Dome
  const domeGrad = ctx.createRadialGradient(-s * 0.2, -s * 0.5, 0, 0, -s * 0.2, s * 0.55);
  domeGrad.addColorStop(0, 'rgba(180,255,220,0.9)');
  domeGrad.addColorStop(0.6, 'rgba(40,180,120,0.7)');
  domeGrad.addColorStop(1, 'rgba(20,100,60,0.6)');
  ctx.fillStyle = domeGrad;
  ctx.beginPath();
  ctx.ellipse(0, -s * 0.15, s * 0.45, s * 0.55, 0, Math.PI, 0);
  ctx.closePath();
  ctx.fill();

  // Rim lights
  for (let i = 0; i < 6; i++) {
    const a = (i / 6) * Math.PI * 2 + t * 2;
    const lx = Math.cos(a) * s * 0.78;
    const ly = Math.sin(a) * s * 0.24;
    ctx.fillStyle = `hsl(${(i * 60 + t * 80) % 360}, 100%, 70%)`;
    ctx.beginPath();
    ctx.arc(lx, ly, s * 0.1, 0, Math.PI * 2);
    ctx.fill();
  }

  ctx.restore();
}

function drawBomber(ctx: CanvasRenderingContext2D, e: Enemy, t: number, scale: number) {
  const s = e.radius * scale;
  ctx.save();
  ctx.translate(e.pos.x, e.pos.y);
  ctx.rotate(e.angle);

  // Main triangular hull
  const hullGrad = ctx.createLinearGradient(0, -s, 0, s);
  hullGrad.addColorStop(0, '#554466');
  hullGrad.addColorStop(1, '#221133');
  ctx.fillStyle = hullGrad;
  ctx.beginPath();
  ctx.moveTo(0, -s * 1.1);
  ctx.lineTo(s * 1.1, s * 0.8);
  ctx.lineTo(0, s * 0.4);
  ctx.lineTo(-s * 1.1, s * 0.8);
  ctx.closePath();
  ctx.fill();

  // Cockpit
  const cpGrad = ctx.createRadialGradient(0, -s * 0.3, 0, 0, -s * 0.3, s * 0.35);
  cpGrad.addColorStop(0, `hsla(${200 + Math.sin(t * 3) * 30}, 100%, 80%, 0.95)`);
  cpGrad.addColorStop(1, 'rgba(80,0,180,0.4)');
  ctx.fillStyle = cpGrad;
  ctx.beginPath();
  ctx.ellipse(0, -s * 0.25, s * 0.3, s * 0.4, 0, 0, Math.PI * 2);
  ctx.fill();

  // Engine glow
  for (let i = -1; i <= 1; i += 2) {
    const eg = ctx.createRadialGradient(i * s * 0.5, s * 0.8, 0, i * s * 0.5, s * 0.8, s * 0.5);
    eg.addColorStop(0, 'rgba(150,50,255,0.9)');
    eg.addColorStop(1, 'rgba(80,0,180,0)');
    ctx.fillStyle = eg;
    ctx.beginPath();
    ctx.arc(i * s * 0.5, s * 0.8, s * 0.5, 0, Math.PI * 2);
    ctx.fill();
  }

  ctx.restore();
}

function drawShuttle(ctx: CanvasRenderingContext2D, shuttle: Shuttle, t: number) {
  const { pos, vel, collected, flashTimer } = shuttle;
  const facingRight = vel.x > 0;
  const s = 28; // base size

  ctx.save();
  ctx.translate(pos.x, pos.y);
  if (!facingRight) ctx.scale(-1, 1); // mirror when flying right-to-left

  // Thruster plume
  const plumeGrad = ctx.createLinearGradient(s * 0.9, 0, s * 1.8, 0);
  plumeGrad.addColorStop(0, 'rgba(255,180,60,0.9)');
  plumeGrad.addColorStop(0.4, 'rgba(255,100,20,0.6)');
  plumeGrad.addColorStop(1, 'rgba(255,60,0,0)');
  ctx.fillStyle = plumeGrad;
  const plumeFlicker = 0.8 + Math.sin(t * 22) * 0.2;
  ctx.beginPath();
  ctx.ellipse(s * 1.35, 0, s * 0.55 * plumeFlicker, s * 0.18, 0, 0, Math.PI * 2);
  ctx.fill();

  // OMS pods (small bumps on fuselage rear)
  ctx.fillStyle = '#8899aa';
  ctx.beginPath();
  ctx.ellipse(-s * 0.35, -s * 0.32, s * 0.18, s * 0.1, 0, 0, Math.PI * 2);
  ctx.fill();

  // Main fuselage
  const bodyGrad = ctx.createLinearGradient(-s * 0.9, -s * 0.3, -s * 0.9, s * 0.3);
  bodyGrad.addColorStop(0, '#e8e8ec');
  bodyGrad.addColorStop(0.4, '#c0c4cc');
  bodyGrad.addColorStop(1, '#8090a0');
  ctx.fillStyle = bodyGrad;
  ctx.beginPath();
  // Nose
  ctx.moveTo(-s * 0.95, 0);
  ctx.bezierCurveTo(-s * 0.95, -s * 0.22, -s * 0.5, -s * 0.32, s * 0.0, -s * 0.3);
  // Tail top
  ctx.lineTo(s * 0.9, -s * 0.3);
  ctx.lineTo(s * 0.9, s * 0.3);
  // Belly back to nose
  ctx.lineTo(s * 0.0, s * 0.3);
  ctx.bezierCurveTo(-s * 0.5, s * 0.32, -s * 0.95, s * 0.22, -s * 0.95, 0);
  ctx.closePath();
  ctx.fill();

  // Cockpit windows
  ctx.fillStyle = 'rgba(160,220,255,0.85)';
  ctx.beginPath();
  ctx.ellipse(-s * 0.72, -s * 0.12, s * 0.14, s * 0.1, -0.3, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = 'rgba(200,240,255,0.5)';
  ctx.beginPath();
  ctx.ellipse(-s * 0.75, -s * 0.15, s * 0.06, s * 0.05, -0.3, 0, Math.PI * 2);
  ctx.fill();

  // Delta wing
  const wingGrad = ctx.createLinearGradient(0, 0, 0, s * 0.9);
  wingGrad.addColorStop(0, '#b0b8c4');
  wingGrad.addColorStop(1, '#607080');
  ctx.fillStyle = wingGrad;
  ctx.beginPath();
  ctx.moveTo(-s * 0.3, s * 0.28);
  ctx.lineTo(s * 0.85, s * 0.28);
  ctx.lineTo(s * 0.4, s * 0.85);
  ctx.lineTo(-s * 0.55, s * 0.85);
  ctx.closePath();
  ctx.fill();

  // Vertical stabilizer
  ctx.fillStyle = '#9aaabb';
  ctx.beginPath();
  ctx.moveTo(s * 0.5, -s * 0.3);
  ctx.lineTo(s * 0.9, -s * 0.3);
  ctx.lineTo(s * 0.9, -s * 0.8);
  ctx.lineTo(s * 0.55, -s * 0.3);
  ctx.closePath();
  ctx.fill();

  // Shield badge on fuselage
  if (!collected) {
    ctx.fillStyle = `rgba(0,255,160,${0.7 + Math.sin(t * 5) * 0.3})`;
    ctx.font = `bold ${s * 0.5}px sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText('🛡', 0, 0);
    ctx.textBaseline = 'alphabetic';
  }

  // Collect flash
  if (flashTimer > 0) {
    ctx.globalAlpha = flashTimer / 0.4;
    ctx.fillStyle = '#ffffff';
    ctx.beginPath();
    ctx.ellipse(0, 0, s * 1.4, s * 1.0, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.globalAlpha = 1;
  }

  ctx.restore();
}

function drawReticle(ctx: CanvasRenderingContext2D, cx: number, cy: number, size: number, t: number, charging: boolean) {
  const r = size;
  const pulse = 0.85 + Math.sin(t * 6) * 0.15;
  const alpha = charging ? 1.0 : 0.85;
  const color = charging ? `rgba(255,80,80,${alpha})` : `rgba(0,255,160,${alpha})`;
  const innerColor = charging ? 'rgba(255,120,120,0.6)' : 'rgba(0,255,160,0.4)';

  ctx.save();
  ctx.translate(cx, cy);
  ctx.rotate(t * 0.8);

  // Outer ring
  ctx.strokeStyle = color;
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.arc(0, 0, r * pulse, 0, Math.PI * 2);
  ctx.stroke();

  // Inner dot
  ctx.fillStyle = innerColor;
  ctx.beginPath();
  ctx.arc(0, 0, r * 0.12, 0, Math.PI * 2);
  ctx.fill();

  // Cross hairs
  ctx.strokeStyle = color;
  ctx.lineWidth = 1.5;
  const gap = r * 0.22;
  const len = r * 0.42;
  // top
  ctx.beginPath();
  ctx.moveTo(0, -gap);
  ctx.lineTo(0, -gap - len);
  ctx.stroke();
  // bottom
  ctx.beginPath();
  ctx.moveTo(0, gap);
  ctx.lineTo(0, gap + len);
  ctx.stroke();
  // left
  ctx.beginPath();
  ctx.moveTo(-gap, 0);
  ctx.lineTo(-gap - len, 0);
  ctx.stroke();
  // right
  ctx.beginPath();
  ctx.moveTo(gap, 0);
  ctx.lineTo(gap + len, 0);
  ctx.stroke();

  // Diagonal corner ticks
  for (let q = 0; q < 4; q++) {
    ctx.save();
    ctx.rotate((q * Math.PI) / 2 + Math.PI / 4);
    ctx.strokeStyle = color;
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.moveTo(r * 0.55, -r * 0.1);
    ctx.lineTo(r * 0.7, -r * 0.1);
    ctx.lineTo(r * 0.7, r * 0.1);
    ctx.stroke();
    ctx.restore();
  }

  ctx.restore();
}

function drawLaser(ctx: CanvasRenderingContext2D, beam: LaserBeam, canvasW: number, canvasH: number) {
  const alpha = Math.max(0, beam.timer / 0.25);
  const originX = canvasW / 2;
  const originY = canvasH * 0.96;

  // Outer glow beam
  ctx.save();
  ctx.globalAlpha = alpha * 0.4;
  const grad = ctx.createLinearGradient(originX, originY, beam.targetX, beam.targetY);
  grad.addColorStop(0, '#ff2020');
  grad.addColorStop(0.5, '#ff8040');
  grad.addColorStop(1, beam.hit ? '#ffffff' : 'rgba(255,80,20,0)');
  ctx.strokeStyle = grad;
  ctx.lineWidth = 14;
  ctx.lineCap = 'round';
  ctx.beginPath();
  ctx.moveTo(originX, originY);
  ctx.lineTo(beam.targetX, beam.targetY);
  ctx.stroke();
  ctx.globalAlpha = 1;

  // Core beam
  ctx.globalAlpha = alpha * 0.9;
  const coreGrad = ctx.createLinearGradient(originX, originY, beam.targetX, beam.targetY);
  coreGrad.addColorStop(0, '#ffffff');
  coreGrad.addColorStop(0.3, '#ff6060');
  coreGrad.addColorStop(1, beam.hit ? '#ffffff' : 'rgba(255,80,20,0)');
  ctx.strokeStyle = coreGrad;
  ctx.lineWidth = 3;
  ctx.beginPath();
  ctx.moveTo(originX, originY);
  ctx.lineTo(beam.targetX, beam.targetY);
  ctx.stroke();
  ctx.globalAlpha = 1;

  // Impact flash
  if (beam.hit) {
    const flashGrad = ctx.createRadialGradient(beam.targetX, beam.targetY, 0, beam.targetX, beam.targetY, 40 * alpha);
    flashGrad.addColorStop(0, `rgba(255,255,200,${alpha})`);
    flashGrad.addColorStop(0.4, `rgba(255,120,40,${alpha * 0.7})`);
    flashGrad.addColorStop(1, 'rgba(255,40,0,0)');
    ctx.fillStyle = flashGrad;
    ctx.beginPath();
    ctx.arc(beam.targetX, beam.targetY, 40 * alpha, 0, Math.PI * 2);
    ctx.fill();
  }

  ctx.restore();
}

function drawParticle(ctx: CanvasRenderingContext2D, p: Particle) {
  const a = p.life / p.maxLife;
  ctx.globalAlpha = a;
  ctx.fillStyle = p.color;
  ctx.beginPath();
  ctx.arc(p.pos.x, p.pos.y, p.radius * a, 0, Math.PI * 2);
  ctx.fill();
  ctx.globalAlpha = 1;
}

// ─── Main Component ───────────────────────────────────────────────────────────

export function SpaceGunGame() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const earthImgRef = useRef<HTMLImageElement | null>(null);
  const laserAudioRef = useRef<HTMLAudioElement | null>(null);
  const explosionAudioRef = useRef<HTMLAudioElement | null>(null);
  const shieldAudioRef = useRef<HTMLAudioElement | null>(null);
  const musicRef = useRef<HTMLAudioElement | null>(null);
  const startAudioRef = useRef<HTMLAudioElement | null>(null);
  const deathAudioRef = useRef<HTMLAudioElement | null>(null);
  const earthStrikeAudioRef = useRef<HTMLAudioElement | null>(null);
  const gameRef = useRef({
    state: 'idle' as GameState,
    score: 0,
    lives: MAX_LIVES,
    wave: 1,
    t: 0,
    dt: 0,
    lastTime: 0,
    reticle: { x: 0, y: 0 } as Vec2,
    enemies: [] as Enemy[],
    lasers: [] as LaserBeam[],
    particles: [] as Particle[],
    stars: [] as StarField[],
    spawnTimer: 0,
    laserCooldown: 0,
    frameId: 0,
    canvasW: 0,
    canvasH: 0,
    charging: false,
    firing: false,          // held-down rapid fire
    earthClouds: [] as { x: number; y: number; r: number; drift: number }[],
    continents: [] as { x: number; y: number; w: number; h: number; c: number }[],
    invincibleTimer: 0,
    shuttles: [] as Shuttle[],
    nextShieldAt: SHIELD_BONUS_INTERVAL,  // score threshold for next shuttle
    shieldBannerTimer: 0,                 // how long to show the +SHIELD banner
  });

  const [displayScore, setDisplayScore] = useState(0);
  const [displayLives, setDisplayLives] = useState(MAX_LIVES);
  const [displayWave, setDisplayWave] = useState(1);
  const [gameState, setGameState] = useState<GameState>('idle');
  const [shieldBanner, setShieldBanner] = useState(false);
  const [shieldsFull, setShieldsFull] = useState(false);

  // ── Spawn helpers ──────────────────────────────────────────────────────────

  const spawnEnemy = useCallback(() => {
    const g = gameRef.current;
    const W = g.canvasW;
    const H = g.canvasH;
    const waveBonus = Math.min(g.wave - 1, 8);

    // Pick spawn edge: top or sides, above the Earth horizon (~60% of canvas height)
    const edge = Math.random();
    let sx: number, sy: number;
    const horizonY = H * 0.62;
    if (edge < 0.5) {
      sx = Math.random() * W;
      sy = -60;
    } else if (edge < 0.75) {
      sx = -60;
      sy = Math.random() * horizonY;
    } else {
      sx = W + 60;
      sy = Math.random() * horizonY;
    }

    // Target near center-ish
    const tx = W * 0.3 + Math.random() * W * 0.4;
    const ty = H * 0.5 + Math.random() * H * 0.25;

    const dx = tx - sx;
    const dy = ty - sy;
    const len = Math.sqrt(dx * dx + dy * dy);
    const speed = (1.5 + Math.random() * 1.2 + waveBonus * 0.25) * (H / 700);

    const typeRoll = Math.random();
    let type: Enemy['type'];
    let radius: number;
    let hp: number;
    let points: number;

    if (typeRoll < 0.5) {
      type = 'missile';
      radius = 18 + Math.random() * 8;
      hp = 1;
      points = 10 + waveBonus * 2;
    } else if (typeRoll < 0.78) {
      type = 'alien';
      radius = 24 + Math.random() * 12;
      hp = 2 + Math.floor(waveBonus / 3);
      points = 25 + waveBonus * 3;
    } else {
      type = 'bomber';
      radius = 30 + Math.random() * 14;
      hp = 3 + Math.floor(waveBonus / 2);
      points = 50 + waveBonus * 5;
    }

    // Angle missiles toward camera
    const angle = type === 'missile'
      ? Math.atan2(dy, dx) + Math.PI / 2
      : Math.random() * Math.PI * 2;

    g.enemies.push({
      id: uid(),
      type,
      pos: { x: sx, y: sy },
      vel: { x: (dx / len) * speed, y: (dy / len) * speed },
      radius,
      hp,
      maxHp: hp,
      angle,
      spinSpeed: (Math.random() - 0.5) * 0.04,
      points,
      exploding: false,
      explodeTimer: 0,
      trail: [],
    });
  }, []);

  const spawnExplosion = useCallback((pos: Vec2, size: number, type: Enemy['type']) => {
    const g = gameRef.current;
    const count = Math.floor(size * 1.5);
    const colors =
      type === 'missile' ? ['#ff4400', '#ff8800', '#ffcc00', '#ffffff', '#ffaa44'] :
      type === 'alien'   ? ['#00ff88', '#44ffcc', '#00cc66', '#88ffaa', '#ffffff'] :
                           ['#9944ff', '#cc88ff', '#6600cc', '#ff44ff', '#ffffff'];

    for (let i = 0; i < count; i++) {
      const speed = (1.5 + Math.random() * 3.5) * (size / 20);
      const angle = Math.random() * Math.PI * 2;
      g.particles.push({
        id: uid(),
        pos: { x: pos.x, y: pos.y },
        vel: {
          x: Math.cos(angle) * speed,
          y: Math.sin(angle) * speed,
        },
        life: 0.6 + Math.random() * 0.8,
        maxLife: 0.6 + Math.random() * 0.8,
        color: colors[Math.floor(Math.random() * colors.length)],
        radius: 2 + Math.random() * 5,
      });
    }
  }, []);

  // ── Fire laser ────────────────────────────────────────────────────────────

  const fireLaser = useCallback((tx: number, ty: number) => {
    const g = gameRef.current;
    if (g.laserCooldown > 0 || g.state !== 'playing') return;
    g.laserCooldown = LASER_COOLDOWN / 1000;

    // Play laser SFX — clone the audio node so rapid shots overlap cleanly
    if (laserAudioRef.current) {
      const sfx = laserAudioRef.current.cloneNode() as HTMLAudioElement;
      sfx.volume = 0.45;
      sfx.play().catch(() => {/* autoplay blocked until first user gesture */});
    }

    // Check for hits
    let hit = false;
    for (const enemy of g.enemies) {
      if (enemy.exploding) continue;
      const d = dist(enemy.pos, { x: tx, y: ty });
      if (d < enemy.radius * 1.4) {
        enemy.hp -= 1;
        hit = true;
        if (enemy.hp <= 0) {
          enemy.exploding = true;
          enemy.explodeTimer = 0.55;
          spawnExplosion(enemy.pos, enemy.radius, enemy.type);
          g.score += enemy.points;
          setDisplayScore(g.score);
          // Play explosion SFX — clone so overlapping kills don't cut each other off
          if (explosionAudioRef.current) {
            const sfx = explosionAudioRef.current.cloneNode() as HTMLAudioElement;
            sfx.volume = 0.6;
            sfx.play().catch(() => {});
          }
        }
        break;
      }
    }

    // Check if laser hits a shuttle → collect the shield upgrade
    if (!hit) {
      for (const sh of g.shuttles) {
        if (sh.collected) continue;
        const d = dist(sh.pos, { x: tx, y: ty });
        if (d < 55) {
          sh.collected = true;
          sh.flashTimer = 0.4;
          const wasAtMax = g.lives >= MAX_SHIELDS;
          if (!wasAtMax) {
            g.lives += 1;
          }
          g.shieldBannerTimer = 2.2;
          setDisplayLives(g.lives);
          setShieldsFull(wasAtMax);
          setShieldBanner(true);
          hit = true;
          // Play shield upgrade SFX
          if (shieldAudioRef.current) {
            const sfx = shieldAudioRef.current.cloneNode() as HTMLAudioElement;
            sfx.volume = 0.7;
            sfx.play().catch(() => {});
          }
          break;
        }
      }
    }

    g.lasers.push({
      id: uid(),
      x: g.canvasW / 2,
      y: g.canvasH * 0.96,
      targetX: tx,
      targetY: ty,
      timer: 0.25,
      hit,
    });
  }, [spawnExplosion]);

  // ── Input handlers ────────────────────────────────────────────────────────

  const getCanvasPos = useCallback((clientX: number, clientY: number): Vec2 => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    return {
      x: (clientX - rect.left) * scaleX,
      y: (clientY - rect.top) * scaleY,
    };
  }, []);

  const startGame = useCallback(() => {
    const g = gameRef.current;
    g.state = 'playing';
    g.score = 0;
    g.lives = MAX_LIVES;
    g.wave = 1;
    g.enemies = [];
    g.lasers = [];
    g.particles = [];
    g.shuttles = [];
    g.spawnTimer = 0;
    g.laserCooldown = 0;
    g.invincibleTimer = 1.5;
    g.firing = false;
    g.nextShieldAt = SHIELD_BONUS_INTERVAL;
    g.shieldBannerTimer = 0;
    setDisplayScore(0);
    setDisplayLives(MAX_LIVES);
    setDisplayWave(1);
    setShieldBanner(false);
    setShieldsFull(false);
    setGameState('playing');
    // Play click-to-enter sound effect
    if (startAudioRef.current) {
      const sfx = startAudioRef.current.cloneNode() as HTMLAudioElement;
      sfx.volume = 0.8;
      sfx.play().catch(() => {});
    }
    // Start background music (restart from beginning on retry)
    if (musicRef.current) {
      musicRef.current.currentTime = 0;
      musicRef.current.play().catch(() => {});
    }
  }, []);

  const handleMouseMove = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
    const pos = getCanvasPos(e.clientX, e.clientY);
    gameRef.current.reticle = pos;
  }, [getCanvasPos]);

  const handleMouseDown = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
    const g = gameRef.current;
    if (g.state === 'idle' || g.state === 'gameover') { startGame(); return; }
    const pos = getCanvasPos(e.clientX, e.clientY);
    g.reticle = pos;
    g.firing = true;
    fireLaser(pos.x, pos.y);
  }, [getCanvasPos, fireLaser, startGame]);

  const handleMouseUp = useCallback(() => {
    gameRef.current.firing = false;
  }, []);

  const handleTouchMove = useCallback((e: React.TouchEvent<HTMLCanvasElement>) => {
    e.preventDefault();
    const t = e.touches[0];
    const pos = getCanvasPos(t.clientX, t.clientY);
    gameRef.current.reticle = pos;
  }, [getCanvasPos]);

  const handleTouchStart = useCallback((e: React.TouchEvent<HTMLCanvasElement>) => {
    e.preventDefault();
    const g = gameRef.current;
    if (g.state === 'idle' || g.state === 'gameover') { startGame(); return; }
    const t = e.touches[0];
    const pos = getCanvasPos(t.clientX, t.clientY);
    g.reticle = pos;
    g.firing = true;
    fireLaser(pos.x, pos.y);
  }, [getCanvasPos, fireLaser, startGame]);

  const handleTouchEnd = useCallback((e: React.TouchEvent<HTMLCanvasElement>) => {
    e.preventDefault();
    gameRef.current.firing = false;
  }, []);

  // Keep click handler for single-tap compatibility
  const handleClick = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
    const g = gameRef.current;
    if (g.state === 'idle' || g.state === 'gameover') { startGame(); return; }
    const pos = getCanvasPos(e.clientX, e.clientY);
    fireLaser(pos.x, pos.y);
  }, [getCanvasPos, fireLaser, startGame]);

  // ── Main game loop ────────────────────────────────────────────────────────

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d')!;
    const g = gameRef.current;

    // Load Earth photo
    const earthImg = new Image();
    earthImg.crossOrigin = 'anonymous';
    earthImg.src = EARTH_IMG_URL;
    earthImg.onload = () => { earthImgRef.current = earthImg; };

    // Preload laser SFX
    const laserAudio = new Audio(LASER_SFX_URL);
    laserAudio.preload = 'auto';
    laserAudioRef.current = laserAudio;

    // Preload explosion SFX
    const explosionAudio = new Audio(EXPLOSION_SFX_URL);
    explosionAudio.preload = 'auto';
    explosionAudioRef.current = explosionAudio;

    // Preload shield upgrade SFX
    const shieldAudio = new Audio(SHIELD_SFX_URL);
    shieldAudio.preload = 'auto';
    shieldAudioRef.current = shieldAudio;

    // Preload background music
    const music = new Audio(MUSIC_URL);
    music.loop = true;
    music.volume = 0.35;
    music.preload = 'auto';
    musicRef.current = music;

    // Preload start / death SFX
    const startAudio = new Audio(START_SFX_URL);
    startAudio.preload = 'auto';
    startAudioRef.current = startAudio;

    const deathAudio = new Audio(DEATH_SFX_URL);
    deathAudio.preload = 'auto';
    deathAudioRef.current = deathAudio;

    // Preload earth strike SFX
    const earthStrikeAudio = new Audio(EARTH_STRIKE_SFX_URL);
    earthStrikeAudio.preload = 'auto';
    earthStrikeAudioRef.current = earthStrikeAudio;

    // Init stars (only in upper space area)
    g.stars = generateStars(STAR_COUNT);
    // Keep earthClouds/continents arrays for compatibility but we won't draw them
    g.earthClouds = [];
    g.continents = [];

    // Resize handler
    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
      g.canvasW = canvas.width;
      g.canvasH = canvas.height;
      g.reticle = { x: canvas.width / 2, y: canvas.height / 2 };
    };
    resize();
    window.addEventListener('resize', resize);

    // ── Draw background ──────────────────────────────────────────────────────

    function drawBackground(W: number, H: number) {
      // Deep space fill (always)
      ctx.fillStyle = '#000008';
      ctx.fillRect(0, 0, W, H);

      // Stars in upper half (space region)
      for (const star of g.stars) {
        star.twinkle += g.dt * 1.2;
        const b = star.brightness * (0.7 + Math.sin(star.twinkle) * 0.3);
        ctx.globalAlpha = b;
        ctx.fillStyle = '#ffffff';
        ctx.beginPath();
        ctx.arc(star.x * W, star.y * H * 0.55, star.size, 0, Math.PI * 2);
        ctx.fill();
        ctx.globalAlpha = 1;
      }

      // Earth photo — fills the lower ~65% of the screen, object-fit cover style
      const earthImg = earthImgRef.current;
      // Image aspect: 1280×720 (landscape). We map it so its top edge sits at ~35% of canvas height
      // and it covers the full width (or height if taller).
      const imgDrawY = H * 0.30;
      const imgDrawH = H - imgDrawY;
      if (earthImg) {
        // Scale to cover the available area
        const imgAspect = earthImg.naturalWidth / earthImg.naturalHeight;
        const areaAspect = W / imgDrawH;
        let dw: number, dh: number, dx: number, dy: number;
        if (areaAspect > imgAspect) {
          // area wider than image — fit width
          dw = W;
          dh = W / imgAspect;
          dx = 0;
          dy = imgDrawY - (dh - imgDrawH) * 0.15; // slight upward shift to show horizon
        } else {
          // area taller — fit height
          dh = imgDrawH;
          dw = imgDrawH * imgAspect;
          dx = (W - dw) / 2;
          dy = imgDrawY;
        }
        ctx.drawImage(earthImg, dx, dy, dw, dh);
      } else {
        // Fallback gradient while image loads
        const grad = ctx.createLinearGradient(0, imgDrawY, 0, H);
        grad.addColorStop(0.00, '#0d1b4b');
        grad.addColorStop(0.15, '#1a2f6e');
        grad.addColorStop(0.30, '#2a5fc8');
        grad.addColorStop(0.45, '#3a7de0');
        grad.addColorStop(0.55, '#1e5c8a');
        grad.addColorStop(0.80, '#0e3d6b');
        grad.addColorStop(1.00, '#051a2e');
        ctx.fillStyle = grad;
        ctx.fillRect(0, imgDrawY, W, H - imgDrawY);
      }

      // Atmosphere glow rim overlay — blends photo edge into space
      const atmTop = H * 0.28;
      const atmBot = H * 0.48;
      const atmGrad = ctx.createLinearGradient(0, atmTop, 0, atmBot);
      atmGrad.addColorStop(0.0, 'rgba(0,0,8,1)');
      atmGrad.addColorStop(0.35, 'rgba(10,30,80,0.55)');
      atmGrad.addColorStop(0.65, 'rgba(30,80,200,0.18)');
      atmGrad.addColorStop(1.0, 'rgba(30,80,200,0)');
      ctx.fillStyle = atmGrad;
      ctx.fillRect(0, atmTop, W, atmBot - atmTop);

      // Subtle vignette on left/right edges
      const vigL = ctx.createLinearGradient(0, 0, W * 0.18, 0);
      vigL.addColorStop(0, 'rgba(0,0,0,0.55)');
      vigL.addColorStop(1, 'rgba(0,0,0,0)');
      ctx.fillStyle = vigL;
      ctx.fillRect(0, 0, W * 0.18, H);
      const vigR = ctx.createLinearGradient(W, 0, W * 0.82, 0);
      vigR.addColorStop(0, 'rgba(0,0,0,0.55)');
      vigR.addColorStop(1, 'rgba(0,0,0,0)');
      ctx.fillStyle = vigR;
      ctx.fillRect(W * 0.82, 0, W * 0.18, H);

      // Gun barrel (first-person cockpit frame)
      drawGunCockpit(W, H);
    }

    function drawGunCockpit(W: number, H: number) {
      // Bottom edge frame
      const frameGrad = ctx.createLinearGradient(0, H * 0.88, 0, H);
      frameGrad.addColorStop(0, '#111a22');
      frameGrad.addColorStop(1, '#0a1018');
      ctx.fillStyle = frameGrad;
      ctx.fillRect(0, H * 0.88, W, H * 0.12);

      // Side panels
      const sidePanelW = W * 0.12;
      ctx.fillStyle = '#0d1520';
      ctx.beginPath();
      ctx.moveTo(0, H * 0.72);
      ctx.lineTo(sidePanelW, H * 0.88);
      ctx.lineTo(0, H * 0.88);
      ctx.closePath();
      ctx.fill();
      ctx.beginPath();
      ctx.moveTo(W, H * 0.72);
      ctx.lineTo(W - sidePanelW, H * 0.88);
      ctx.lineTo(W, H * 0.88);
      ctx.closePath();
      ctx.fill();

      // Gun barrel
      const bw = W * 0.035;
      const barrelGrad = ctx.createLinearGradient(W / 2 - bw, 0, W / 2 + bw, 0);
      barrelGrad.addColorStop(0, '#1a2a3a');
      barrelGrad.addColorStop(0.3, '#2a4a6a');
      barrelGrad.addColorStop(0.5, '#4a7aaa');
      barrelGrad.addColorStop(0.7, '#2a4a6a');
      barrelGrad.addColorStop(1, '#1a2a3a');
      ctx.fillStyle = barrelGrad;
      ctx.fillRect(W / 2 - bw, H * 0.88, bw * 2, H * 0.12);

      // Barrel tip glow
      const glowGrad = ctx.createRadialGradient(W / 2, H * 0.95, 0, W / 2, H * 0.95, bw * 3);
      const cooldown = g.laserCooldown;
      const r = Math.floor(lerp(40, 255, Math.min(cooldown * 4, 1)));
      const gb = Math.floor(lerp(180, 60, Math.min(cooldown * 4, 1)));
      glowGrad.addColorStop(0, `rgba(${r},${gb},${gb},0.8)`);
      glowGrad.addColorStop(1, 'rgba(0,0,0,0)');
      ctx.fillStyle = glowGrad;
      ctx.beginPath();
      ctx.arc(W / 2, H * 0.95, bw * 3, 0, Math.PI * 2);
      ctx.fill();

      // HUD panel lines
      ctx.strokeStyle = 'rgba(40,160,255,0.3)';
      ctx.lineWidth = 1;
      // Left HUD
      ctx.beginPath();
      ctx.moveTo(W * 0.02, H * 0.89);
      ctx.lineTo(W * 0.22, H * 0.89);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(W * 0.02, H * 0.93);
      ctx.lineTo(W * 0.18, H * 0.93);
      ctx.stroke();
      // Right HUD
      ctx.beginPath();
      ctx.moveTo(W * 0.98, H * 0.89);
      ctx.lineTo(W * 0.78, H * 0.89);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(W * 0.98, H * 0.93);
      ctx.lineTo(W * 0.82, H * 0.93);
      ctx.stroke();
    }

    // ── Update logic ──────────────────────────────────────────────────────────

    function update(now: number) {
      const dt = Math.min((now - g.lastTime) / 1000, 0.05);
      g.lastTime = now;
      g.dt = dt;
      g.t += dt;

      if (g.state !== 'playing') return;

      // Cooldown timers
      if (g.laserCooldown > 0) g.laserCooldown -= dt;
      if (g.invincibleTimer > 0) g.invincibleTimer -= dt;
      if (g.shieldBannerTimer > 0) {
        g.shieldBannerTimer -= dt;
        if (g.shieldBannerTimer <= 0) setShieldBanner(false);
      }

      // Rapid-fire: auto-shoot while button held
      if (g.firing && g.laserCooldown <= 0) {
        fireLaser(g.reticle.x, g.reticle.y);
      }

      // Shuttle spawn — every SHIELD_BONUS_INTERVAL points
      if (g.score >= g.nextShieldAt && g.shuttles.length === 0) {
        g.nextShieldAt += SHIELD_BONUS_INTERVAL;
        const W = g.canvasW;
        const H = g.canvasH;
        // Fly at ~20-35% of screen height (upper space zone)
        const flyY = H * (0.15 + Math.random() * 0.18);
        const fromLeft = Math.random() < 0.5;
        const speed = W * 0.0018; // crosses screen in ~3.5s at 60fps
        g.shuttles.push({
          id: uid(),
          pos: { x: fromLeft ? -80 : W + 80, y: flyY },
          vel: { x: fromLeft ? speed : -speed, y: 0 },
          collected: false,
          gone: false,
          flashTimer: 0,
        });
      }

      // Update shuttles
      for (let i = g.shuttles.length - 1; i >= 0; i--) {
        const sh = g.shuttles[i];
        if (sh.flashTimer > 0) sh.flashTimer -= dt;

        if (!sh.collected) {
          sh.pos.x += sh.vel.x;
          sh.pos.y += sh.vel.y;
        }

        // Off screen — gone without collecting (no penalty, just missed)
        const W2 = g.canvasW;
        if (sh.pos.x < -150 || sh.pos.x > W2 + 150) {
          g.shuttles.splice(i, 1);
        }
        // Remove after flash completes
        if (sh.collected && sh.flashTimer <= 0) {
          g.shuttles.splice(i, 1);
        }
      }

      // Wave progression
      const waveThreshold = 100 + (g.wave - 1) * 150;
      if (g.score >= waveThreshold * g.wave) {
        g.wave++;
        setDisplayWave(g.wave);
      }

      // Spawn enemies
      const spawnInterval = Math.max(
        MIN_SPAWN_INTERVAL,
        BASE_SPAWN_INTERVAL - (g.wave - 1) * 180
      ) / 1000;
      g.spawnTimer += dt;
      if (g.spawnTimer >= spawnInterval) {
        g.spawnTimer = 0;
        spawnEnemy();
        // Extra spawns at higher waves
        if (g.wave >= 3 && Math.random() < 0.35) spawnEnemy();
        if (g.wave >= 6 && Math.random() < 0.3) spawnEnemy();
      }

      // Update enemies
      const W = g.canvasW;
      const H = g.canvasH;
      const survivorZone = H * 0.90;

      for (let i = g.enemies.length - 1; i >= 0; i--) {
        const e = g.enemies[i];

        if (e.exploding) {
          e.explodeTimer -= dt;
          if (e.explodeTimer <= 0) {
            g.enemies.splice(i, 1);
          }
          continue;
        }

        e.pos.x += e.vel.x;
        e.pos.y += e.vel.y;
        e.angle += e.spinSpeed;

        // Trail for missiles
        if (e.type === 'missile') {
          e.trail.push({ x: e.pos.x, y: e.pos.y });
          if (e.trail.length > 18) e.trail.shift();
        }

        // Reached the bottom (past Earth horizon) → lose a life
        if (e.pos.y > survivorZone && g.invincibleTimer <= 0) {
          spawnExplosion(e.pos, e.radius * 2, e.type);
          g.enemies.splice(i, 1);
          g.lives -= 1;
          setDisplayLives(g.lives);
          // Play earth strike SFX
          if (earthStrikeAudioRef.current) {
            const sfx = earthStrikeAudioRef.current.cloneNode() as HTMLAudioElement;
            sfx.volume = 0.75;
            sfx.play().catch(() => {});
          }
          if (g.lives <= 0) {
            g.state = 'gameover';
            setGameState('gameover');
            // Play mission-failed sound
            if (deathAudioRef.current) {
              const sfx = deathAudioRef.current.cloneNode() as HTMLAudioElement;
              sfx.volume = 0.85;
              sfx.play().catch(() => {});
            }
            // Fade out and stop background music
            if (musicRef.current) {
              const m = musicRef.current;
              const fadeOut = setInterval(() => {
                if (m.volume > 0.05) {
                  m.volume = Math.max(0, m.volume - 0.05);
                } else {
                  m.pause();
                  m.volume = 0.35; // restore for next game
                  clearInterval(fadeOut);
                }
              }, 80);
            }
          }
          continue;
        }

        // Off screen sides — remove silently
        if (e.pos.x < -120 || e.pos.x > W + 120 || e.pos.y < -120) {
          g.enemies.splice(i, 1);
        }
      }

      // Update lasers
      for (let i = g.lasers.length - 1; i >= 0; i--) {
        g.lasers[i].timer -= dt;
        if (g.lasers[i].timer <= 0) g.lasers.splice(i, 1);
      }

      // Update particles
      for (let i = g.particles.length - 1; i >= 0; i--) {
        const p = g.particles[i];
        p.pos.x += p.vel.x;
        p.pos.y += p.vel.y;
        p.vel.y += 0.05;
        p.life -= dt;
        if (p.life <= 0) g.particles.splice(i, 1);
      }
    }

    // ── Render ─────────────────────────────────────────────────────────────

    function render() {
      const W = g.canvasW;
      const H = g.canvasH;
      ctx.clearRect(0, 0, W, H);

      drawBackground(W, H);

      // Enemy trails
      for (const e of g.enemies) {
        if (e.type === 'missile' && e.trail.length > 2) {
          ctx.save();
          for (let i = 1; i < e.trail.length; i++) {
            const a = (i / e.trail.length) * 0.6;
            ctx.globalAlpha = a;
            ctx.strokeStyle = '#ff8830';
            ctx.lineWidth = (i / e.trail.length) * 3;
            ctx.beginPath();
            ctx.moveTo(e.trail[i - 1].x, e.trail[i - 1].y);
            ctx.lineTo(e.trail[i].x, e.trail[i].y);
            ctx.stroke();
          }
          ctx.restore();
        }
      }

      // Enemies
      const scale = Math.min(W / 400, H / 700, 1.5);
      for (const e of g.enemies) {
        if (e.exploding) {
          // Expanding flash ring
          const prog = 1 - e.explodeTimer / 0.55;
          ctx.globalAlpha = 1 - prog;
          ctx.strokeStyle = '#ffffff';
          ctx.lineWidth = 4;
          ctx.beginPath();
          ctx.arc(e.pos.x, e.pos.y, e.radius * (1 + prog * 2), 0, Math.PI * 2);
          ctx.stroke();
          ctx.globalAlpha = 1;
          continue;
        }

        if (e.type === 'missile') drawMissile(ctx, e, scale);
        else if (e.type === 'alien') drawAlien(ctx, e, g.t, scale);
        else drawBomber(ctx, e, g.t, scale);

        // HP bar for multi-hp enemies
        if (e.maxHp > 1) {
          const barW = e.radius * scale * 2;
          const barH = 4;
          const barX = e.pos.x - barW / 2;
          const barY = e.pos.y - e.radius * scale - 10;
          ctx.fillStyle = 'rgba(0,0,0,0.5)';
          ctx.fillRect(barX, barY, barW, barH);
          ctx.fillStyle = `hsl(${(e.hp / e.maxHp) * 120}, 100%, 50%)`;
          ctx.fillRect(barX, barY, barW * (e.hp / e.maxHp), barH);
        }
      }

      // Particles
      for (const p of g.particles) {
        drawParticle(ctx, p);
      }

      // Laser beams
      for (const beam of g.lasers) {
        drawLaser(ctx, beam, W, H);
      }

      // Shuttles
      for (const sh of g.shuttles) {
        drawShuttle(ctx, sh, g.t);
        // Draw a subtle halo/beacon arrow below shuttle if not yet collected
        if (!sh.collected) {
          const pulse = 0.5 + Math.sin(g.t * 6) * 0.5;
          ctx.save();
          ctx.globalAlpha = pulse * 0.7;
          ctx.fillStyle = '#00ffaa';
          ctx.font = `bold ${14}px sans-serif`;
          ctx.textAlign = 'center';
          ctx.fillText('▼ SHIELD DROP ▼', sh.pos.x, sh.pos.y + 52);
          ctx.globalAlpha = 1;
          ctx.restore();
        }
      }

      // Reticle (only during gameplay)
      if (g.state === 'playing') {
        drawReticle(ctx, g.reticle.x, g.reticle.y, 28, g.t, g.laserCooldown > 0);
      }

      // Overlay UIs
      if (g.state === 'idle') {
        drawTitleScreen(W, H);
      } else if (g.state === 'gameover') {
        drawGameOver(W, H);
      }
    }

    function drawTitleScreen(W: number, H: number) {
      ctx.fillStyle = 'rgba(0,0,0,0.55)';
      ctx.fillRect(0, 0, W, H * 0.62);

      const titleSize = Math.min(W * 0.09, 54);
      ctx.font = `bold ${titleSize}px 'Courier New', monospace`;
      ctx.textAlign = 'center';

      // Glow effect for title
      ctx.shadowColor = '#00ffcc';
      ctx.shadowBlur = 24;
      ctx.fillStyle = '#00ffcc';
      ctx.fillText('ORBITAL LASER', W / 2, H * 0.22);
      ctx.fillText('DEFENSE', W / 2, H * 0.22 + titleSize * 1.2);
      ctx.shadowBlur = 0;

      const subSize = Math.min(W * 0.04, 20);
      ctx.font = `${subSize}px 'Courier New', monospace`;
      ctx.fillStyle = 'rgba(180,230,255,0.85)';
      ctx.fillText('Protect Earth from orbit. Destroy all threats.', W / 2, H * 0.38);

      const tapSize = Math.min(W * 0.045, 22);
      ctx.font = `bold ${tapSize}px 'Courier New', monospace`;
      const pulse = 0.6 + Math.sin(g.t * 3) * 0.4;
      ctx.fillStyle = `rgba(255,220,40,${pulse})`;
      ctx.fillText('[ TAP / CLICK TO BEGIN ]', W / 2, H * 0.50);

      ctx.textAlign = 'left';
    }

    function drawGameOver(W: number, H: number) {
      ctx.fillStyle = 'rgba(0,0,0,0.65)';
      ctx.fillRect(0, 0, W, H * 0.62);

      const titleSize = Math.min(W * 0.09, 52);
      ctx.font = `bold ${titleSize}px 'Courier New', monospace`;
      ctx.textAlign = 'center';
      ctx.shadowColor = '#ff3030';
      ctx.shadowBlur = 28;
      ctx.fillStyle = '#ff4444';
      ctx.fillText('MISSION FAILED', W / 2, H * 0.22);
      ctx.shadowBlur = 0;

      const scoreSize = Math.min(W * 0.06, 32);
      ctx.font = `bold ${scoreSize}px 'Courier New', monospace`;
      ctx.fillStyle = '#ffdd44';
      ctx.fillText(`FINAL SCORE: ${g.score}`, W / 2, H * 0.35);

      const tapSize = Math.min(W * 0.045, 22);
      ctx.font = `bold ${tapSize}px 'Courier New', monospace`;
      const pulse = 0.6 + Math.sin(g.t * 3) * 0.4;
      ctx.fillStyle = `rgba(100,220,255,${pulse})`;
      ctx.fillText('[ TAP / CLICK TO RETRY ]', W / 2, H * 0.48);
      ctx.textAlign = 'left';
    }

    // ── Loop ──────────────────────────────────────────────────────────────

    function loop(now: number) {
      update(now);
      render();
      g.frameId = requestAnimationFrame(loop);
    }

    g.lastTime = performance.now();
    g.frameId = requestAnimationFrame(loop);

    return () => {
      cancelAnimationFrame(g.frameId);
      window.removeEventListener('resize', resize);
      if (musicRef.current) {
        musicRef.current.pause();
        musicRef.current.currentTime = 0;
      }
    };
  }, [spawnEnemy, spawnExplosion, fireLaser]);

  // ─── JSX ───────────────────────────────────────────────────────────────────

  return (
    <div className="relative w-screen h-screen overflow-hidden bg-black select-none">
      <style>{`
        @keyframes shieldBannerAnim {
          0%   { opacity: 0; transform: translateX(-50%) translateY(-12px) scale(0.88); }
          12%  { opacity: 1; transform: translateX(-50%) translateY(0px) scale(1.06); }
          25%  { opacity: 1; transform: translateX(-50%) translateY(0px) scale(1); }
          75%  { opacity: 1; transform: translateX(-50%) translateY(0px) scale(1); }
          100% { opacity: 0; transform: translateX(-50%) translateY(-10px) scale(0.95); }
        }
      `}</style>
      <canvas
        ref={canvasRef}
        className="absolute inset-0 w-full h-full touch-none"
        onMouseMove={handleMouseMove}
        onMouseDown={handleMouseDown}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onClick={handleClick}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        style={{ cursor: 'none' }}
      />

      {/* HUD overlay */}
      {gameState === 'playing' && (
        <div className="absolute inset-0 pointer-events-none">
          {/* Score */}
          <div
            className="absolute top-3 left-1/2 -translate-x-1/2 text-center"
            style={{ fontFamily: "'Courier New', monospace" }}
          >
            <div className="text-xs tracking-widest text-cyan-400 opacity-70">SCORE</div>
            <div
              className="font-bold text-yellow-300 drop-shadow-lg"
              style={{
                fontSize: 'clamp(22px, 6vw, 42px)',
                textShadow: '0 0 12px rgba(255,220,0,0.8)',
              }}
            >
              {displayScore.toLocaleString()}
            </div>
          </div>

          {/* Wave indicator */}
          <div
            className="absolute top-3 right-4"
            style={{ fontFamily: "'Courier New', monospace" }}
          >
            <div className="text-xs tracking-widest text-cyan-400 opacity-70 text-right">WAVE</div>
            <div
              className="font-bold text-cyan-300 text-right"
              style={{
                fontSize: 'clamp(18px, 4vw, 28px)',
                textShadow: '0 0 10px rgba(0,220,255,0.8)',
              }}
            >
              {displayWave}
            </div>
          </div>

          {/* Lives — always show all MAX_SHIELDS slots */}
          <div
            className="absolute top-3 left-4"
            style={{ fontFamily: "'Courier New', monospace" }}
          >
            <div className="text-xs tracking-widest text-cyan-400 opacity-70">
              SHIELDS {displayLives}/{MAX_SHIELDS}
            </div>
            <div className="flex flex-wrap gap-0.5 mt-0.5" style={{ maxWidth: '180px' }}>
              {Array.from({ length: MAX_SHIELDS }).map((_, i) => (
                <div
                  key={i}
                  className="text-lg"
                  style={{
                    textShadow: i < displayLives
                      ? '0 0 8px rgba(0,255,160,0.9)'
                      : 'none',
                    opacity: i < displayLives ? 1 : 0.15,
                    transition: 'opacity 0.3s, text-shadow 0.3s',
                  }}
                >
                  🛡️
                </div>
              ))}
            </div>
          </div>

          {/* Shield bonus banner */}
          {shieldBanner && (
            <div
              className="absolute left-1/2 -translate-x-1/2 pointer-events-none"
              style={{
                top: '18%',
                fontFamily: "'Courier New', monospace",
                animation: 'shieldBannerAnim 2.2s ease-out forwards',
              }}
            >
              <div
                className="px-6 py-2 rounded-lg text-center"
                style={{
                  background: shieldsFull ? 'rgba(40,30,0,0.85)' : 'rgba(0,40,20,0.85)',
                  border: shieldsFull ? '2px solid #ffcc00' : '2px solid #00ffaa',
                  boxShadow: shieldsFull ? '0 0 24px rgba(255,200,0,0.6)' : '0 0 24px rgba(0,255,160,0.6)',
                  fontSize: 'clamp(16px, 4vw, 26px)',
                  color: shieldsFull ? '#ffdd44' : '#00ffcc',
                  textShadow: shieldsFull ? '0 0 12px rgba(255,220,0,1)' : '0 0 12px rgba(0,255,160,1)',
                  whiteSpace: 'nowrap',
                }}
              >
                {shieldsFull ? '🛡️ SHIELDS MAXED OUT!' : '🛡️ +1 SHIELD UPGRADE'}
              </div>
            </div>
          )}

          {/* Enemy type legend */}
          <div
            className="absolute bottom-3 left-4 text-xs opacity-60"
            style={{ fontFamily: "'Courier New', monospace", color: '#88ccff' }}
          >
            <div>▲ MISSILE  +10</div>
            <div>◈ ALIEN    +25</div>
            <div>◆ BOMBER   +50</div>
          </div>

          {/* Mobile fire hint */}
          <div
            className="absolute bottom-3 right-4 text-xs opacity-50 text-right"
            style={{ fontFamily: "'Courier New', monospace", color: '#88ccff' }}
          >
            HOLD TO FIRE
          </div>
        </div>
      )}


    </div>
  );
}
